import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { ConfirmationDialogService } from '@components/base-elements/confirmation-dialog/confirmation-dialog.service';
import { PmButtonComponent, PmIconComponent } from '@libs/ui';

import { selectIsLoggedInUserAdmin } from '@core/store/auth/auth.selectors';
import {
  LucideDownload,
  LucideEllipsisVertical,
  LucidePlus,
  LucideRotateCcw,
  LucideTrash,
} from '@lucide/angular';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AnalysisConfigurationPickerComponent } from 'demand-forecasting/analysis/components/analysis-configuration-picker/analysis-configuration-picker.component';
import { AnalysisVersionPickerComponent } from 'demand-forecasting/analysis/components/analysis-version-picker/analysis-version-picker.component';
import { CreateAnalysisConfigurationDialogComponent } from 'demand-forecasting/analysis/components/configuration/create-analysis-configuration-dialog.component';
import { AnalysisExportDialogComponent } from 'demand-forecasting/analysis/components/dialogs/analysis-export-dialog/analysis-export-dialog.component';
import { AnalysisMutationsService } from 'demand-forecasting/analysis/services/analysis-mutations.service';
import { AnalysisNavigationService } from 'demand-forecasting/analysis/services/analysis-navigation.service';
import { AnalysisStateService } from 'demand-forecasting/analysis/services/analysis-state.service';
import {
  selectAnalysisConfigurationIdByQueryParam,
  selectAnalysisVersionIdByQueryParam,
} from 'demand-forecasting/store/router-state/router.selectors';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'pm-analysis-header',
  templateUrl: './analysis-header.component.html',
  imports: [
    TranslateModule,
    PmButtonComponent,
    AnalysisConfigurationPickerComponent,
    AnalysisVersionPickerComponent,
    MatMenuModule,
    PmIconComponent,
  ],
})
export class AnalysisHeaderComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  protected readonly analysisStateService = inject(AnalysisStateService);
  protected readonly analysisMutationsService = inject(
    AnalysisMutationsService,
  );
  private readonly analysisNavigationService = inject(
    AnalysisNavigationService,
  );

  private readonly confirmationDialogService = inject(
    ConfirmationDialogService,
  );
  private readonly toastService = inject(ToastrService);
  private readonly translate = inject(TranslateService);

  protected readonly plusIcon = LucidePlus;
  protected readonly moreIcon = LucideEllipsisVertical;
  protected readonly recalculateVersionIcon = LucideRotateCcw;
  protected readonly deleteIcon = LucideTrash;
  protected readonly downloadIcon = LucideDownload;

  protected readonly analysisConfigurationId = this.store.selectSignal(
    selectAnalysisConfigurationIdByQueryParam,
  );
  protected readonly analysisVersionId = this.store.selectSignal(
    selectAnalysisVersionIdByQueryParam,
  );

  protected readonly createAnalysisAllowed = this.store.selectSignal(
    selectIsLoggedInUserAdmin,
  );

  protected handleCreateErrorConfiguration() {
    this.dialog.open(CreateAnalysisConfigurationDialogComponent);
  }

  protected handleDeleteVersion(versionId: string): void {
    this.confirmationDialogService.launchDialog({
      onApprove: () =>
        firstValueFrom(
          this.analysisMutationsService.deleteAnalysisVersion(versionId),
        ).then(() =>
          this.toastService.info(
            this.translate.instant('ANALYSIS.DELETE_VERSION_SUCCESS'),
          ),
        ),
      headline: 'ANALYSIS.DELETE_VERSION',
      description: 'ANALYSIS.DELETE_VERSION_CONFIRM',
      variant: 'delete',
    });
  }

  protected handleDeleteErrorConfiguration(analysisConfigurationId: string) {
    this.confirmationDialogService.launchDialog({
      onApprove: () =>
        firstValueFrom(
          this.analysisMutationsService.deleteAnalysisConfiguration(
            analysisConfigurationId,
          ),
        ).then(() =>
          this.toastService.info(
            this.translate.instant(
              'ANALYSIS.DELETE_ERROR_CONFIGURATION_SUCCESS',
            ),
          ),
        ),
      headline: 'ANALYSIS.DELETE_ERROR_CONFIGURATION',
      description: 'ANALYSIS.DELETE_ERROR_CONFIGURATION_CONFIRM',
      variant: 'delete',
    });
  }

  protected handleCreateVersion(analysisConfigurationId: string) {
    this.analysisMutationsService
      .createAnalysisVersion(analysisConfigurationId)
      .subscribe((res) => {
        if (res) {
          this.analysisNavigationService.setQueryParams({
            analysisConfigurationId: res.analysisConfigurationId,
            versionId: res.id,
            hierarchy: null,
            filter: null,
          });
        }
      });
  }

  protected openExportDialog(): void {
    this.dialog.open(AnalysisExportDialogComponent);
  }
}
